import { useMutation, useQueryClient } from '@tanstack/react-query';

import { coverageService } from '../services/coverage.service';

import type { ErrorResponse } from '../services/api.types';

type MoveCoverageToExistingComboParams = {
  comboId: number;
  coverageId: number;
  targetComboId: number;
  note: string;
};

type MoveCoverageToNewComboParams = {
  comboId: number;
  coverageId: number;
  note: string;
};

type MoveCoveragesToExistingComboBatchParams = {
  comboId: number;
  coverageIds: number[];
  targetComboId: number;
  note: string;
};

type MoveCoveragesToNewComboBatchParams = {
  comboId: number;
  coverageIds: number[];
  note: string;
};

type MoveCoverageResponse = {
  newCoverageId?: number;
  newComboId?: number;
};

type MoveCoverageBatchResponse = {
  destinationComboId: number;
  movedCoverageIds: number[];
  primaryCoverageId: number;
};

export const useMoveCoverageToExistingComboMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<MoveCoverageResponse, ErrorResponse, MoveCoverageToExistingComboParams>({
    mutationFn: async (params) => {
      const response = await coverageService.moveCoverageToExistingCombo(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Refresh affected combo lists, but avoid refetching old coverage route after move.
      void Promise.all([
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', variables.comboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', data.newComboId],
          type: 'all',
        }),
      ]);

      queryClient.removeQueries({
        queryKey: ['coverage', variables.comboId, variables.coverageId],
      });

      queryClient.invalidateQueries({
        queryKey: ['coverage', data.newComboId, data.newCoverageId],
        refetchType: 'none',
      });
    },
  });
};

export const useMoveCoverageToNewComboMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<MoveCoverageResponse, ErrorResponse, MoveCoverageToNewComboParams>({
    mutationFn: async (params) => {
      const response = await coverageService.moveCoverageToNewCombo(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', variables.comboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', data.newComboId],
          type: 'all',
        }),
      ]);

      queryClient.removeQueries({
        queryKey: ['coverage', variables.comboId, variables.coverageId],
      });

      queryClient.invalidateQueries({
        queryKey: ['coverage', data.newComboId, data.newCoverageId],
        refetchType: 'none',
      });
    },
  });
};

export const useMoveCoveragesToExistingComboBatchMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<MoveCoverageBatchResponse, ErrorResponse, MoveCoveragesToExistingComboBatchParams>({
    mutationFn: async (params) => {
      const response = await coverageService.moveCoveragesToExistingComboBatch(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', variables.comboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', data.destinationComboId],
          type: 'all',
        }),
      ]);

      variables.coverageIds.forEach((coverageId) => {
        queryClient.removeQueries({
          queryKey: ['coverage', variables.comboId, coverageId],
        });
      });

      queryClient.invalidateQueries({
        queryKey: ['coverage', data.destinationComboId, data.primaryCoverageId],
        refetchType: 'none',
      });
    },
  });
};

export const useMoveCoveragesToNewComboBatchMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<MoveCoverageBatchResponse, ErrorResponse, MoveCoveragesToNewComboBatchParams>({
    mutationFn: async (params) => {
      const response = await coverageService.moveCoveragesToNewComboBatch(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', variables.comboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', data.destinationComboId],
          type: 'all',
        }),
      ]);

      variables.coverageIds.forEach((coverageId) => {
        queryClient.removeQueries({
          queryKey: ['coverage', variables.comboId, coverageId],
        });
      });

      queryClient.invalidateQueries({
        queryKey: ['coverage', data.destinationComboId, data.primaryCoverageId],
        refetchType: 'none',
      });
    },
  });
};
